import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, MessageSquare, FileText, Upload, Plus, Edit, Trash2, 
  Check, X, LogOut, Eye, EyeOff, Save, RefreshCw, Home, Settings, Image, Handshake, Mail, Download, Rocket, Newspaper, Calendar
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('honorees');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [honorees, setHonorees] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [aboutContent, setAboutContent] = useState({});
  const [nominations, setNominations] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ logo_url: '' });
  const [logoUploading, setLogoUploading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [signups, setSignups] = useState([]);
  const [newsletterSignups, setNewsletterSignups] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [homepageContent, setHomepageContent] = useState({});
  const [specialEventsContent, setSpecialEventsContent] = useState({});
  
  // Edit states
  const [editingHonoree, setEditingHonoree] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingInitiative, setEditingInitiative] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [newHonoree, setNewHonoree] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState(null);
  const [newPartner, setNewPartner] = useState(null);
  const [newInitiative, setNewInitiative] = useState(null);
  const [newNews, setNewNews] = useState(null);

  const getAuthHeader = useCallback(() => {
    return {
      auth: {
        username: credentials.username,
        password: credentials.password
      }
    };
  }, [credentials]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await axios.get(`${API}/admin/verify`, getAuthHeader());
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', JSON.stringify(credentials));
    } catch (err) {
      setAuthError('Invalid credentials. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    localStorage.removeItem('adminAuth');
  };

  // Check for stored auth on mount
  useEffect(() => {
    const stored = localStorage.getItem('adminAuth');
    if (stored) {
      const parsed = JSON.parse(stored);
      setCredentials(parsed);
      // Verify stored credentials
      axios.get(`${API}/admin/verify`, {
        auth: { username: parsed.username, password: parsed.password }
      }).then(() => {
        setIsAuthenticated(true);
      }).catch(() => {
        localStorage.removeItem('adminAuth');
      });
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [honoreesRes, testimonialsRes, aboutRes, nominationsRes, settingsRes, partnersRes, signupsRes, newsletterRes, initiativesRes, newsRes, homepageRes, specialEventsRes] = await Promise.all([
        axios.get(`${API}/admin/honorees`, getAuthHeader()),
        axios.get(`${API}/admin/testimonials`, getAuthHeader()),
        axios.get(`${API}/about-content`),
        axios.get(`${API}/admin/nominations`, getAuthHeader()),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/admin/partners`, getAuthHeader()),
        axios.get(`${API}/admin/anniversary-signups`, getAuthHeader()),
        axios.get(`${API}/admin/newsletter-signups`, getAuthHeader()),
        axios.get(`${API}/admin/initiatives`, getAuthHeader()),
        axios.get(`${API}/admin/news`, getAuthHeader()),
        axios.get(`${API}/admin/homepage-content`, getAuthHeader()),
        axios.get(`${API}/admin/special-events-content`, getAuthHeader())
      ]);
      setHonorees(honoreesRes.data);
      setTestimonials(testimonialsRes.data);
      setAboutContent(aboutRes.data);
      setNominations(nominationsRes.data);
      setSiteSettings(settingsRes.data);
      setPartners(partnersRes.data);
      setSignups(signupsRes.data);
      setNewsletterSignups(newsletterRes.data);
      setInitiatives(initiativesRes.data);
      setNewsArticles(newsRes.data);
      setHomepageContent(homepageRes.data);
      setSpecialEventsContent(specialEventsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Honoree CRUD
  const saveHonoree = async (honoree, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/honorees`, honoree, getAuthHeader());
      } else {
        await axios.put(`${API}/admin/honorees/${honoree.id}`, honoree, getAuthHeader());
      }
      fetchAllData();
      setEditingHonoree(null);
      setNewHonoree(null);
    } catch (err) {
      alert('Error saving honoree');
    }
  };

  const deleteHonoree = async (id) => {
    if (!window.confirm('Are you sure you want to delete this honoree?')) return;
    try {
      await axios.delete(`${API}/admin/honorees/${id}`, getAuthHeader());
      fetchAllData();
    } catch (err) {
      alert('Error deleting honoree');
    }
  };

  // Testimonial CRUD
  const saveTestimonial = async (testimonial, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/testimonials`, testimonial, getAuthHeader());
      } else {
        await axios.put(`${API}/admin/testimonials/${testimonial.id}`, testimonial, getAuthHeader());
      }
      fetchAllData();
      setEditingTestimonial(null);
      setNewTestimonial(null);
    } catch (err) {
      alert('Error saving testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, getAuthHeader());
      fetchAllData();
    } catch (err) {
      alert('Error deleting testimonial');
    }
  };

  // Partner CRUD
  const savePartner = async (partner, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/partners`, partner, getAuthHeader());
      } else {
        await axios.put(`${API}/admin/partners/${partner.id}`, partner, getAuthHeader());
      }
      fetchAllData();
      setEditingPartner(null);
      setNewPartner(null);
    } catch (err) {
      alert('Error saving partner');
    }
  };

  const deletePartner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await axios.delete(`${API}/admin/partners/${id}`, getAuthHeader());
      fetchAllData();
    } catch (err) {
      alert('Error deleting partner');
    }
  };

  // Initiative CRUD
  const saveInitiative = async (initiative, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/initiatives`, initiative, getAuthHeader());
      } else {
        await axios.put(`${API}/admin/initiatives/${initiative.id}`, initiative, getAuthHeader());
      }
      fetchAllData();
      setEditingInitiative(null);
      setNewInitiative(null);
    } catch (err) {
      alert('Error saving initiative');
    }
  };

  const deleteInitiative = async (id) => {
    if (!window.confirm('Are you sure you want to delete this initiative?')) return;
    try {
      await axios.delete(`${API}/admin/initiatives/${id}`, getAuthHeader());
      fetchAllData();
    } catch (err) {
      alert('Error deleting initiative');
    }
  };

  // News Article CRUD
  const saveNews = async (article, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/news`, article, getAuthHeader());
      } else {
        await axios.put(`${API}/admin/news/${article.id}`, article, getAuthHeader());
      }
      fetchAllData();
      setEditingNews(null);
      setNewNews(null);
    } catch (err) {
      alert('Error saving news article');
    }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`, getAuthHeader());
      fetchAllData();
    } catch (err) {
      alert('Error deleting news article');
    }
  };

  // About Content
  const saveAboutContent = async () => {
    try {
      await axios.put(`${API}/admin/about-content`, aboutContent, getAuthHeader());
      alert('About content saved successfully!');
    } catch (err) {
      alert('Error saving about content');
    }
  };

  // Homepage Content
  const saveHomepageContent = async () => {
    try {
      await axios.put(`${API}/admin/homepage-content`, homepageContent, getAuthHeader());
      alert('Homepage content saved successfully!');
    } catch (err) {
      alert('Error saving homepage content');
    }
  };

  // Special Events Content
  const saveSpecialEventsContent = async () => {
    try {
      await axios.put(`${API}/admin/special-events-content`, specialEventsContent, getAuthHeader());
      alert('Special Events content saved successfully!');
    } catch (err) {
      alert('Error saving special events content');
    }
  };

  // Image Upload
  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        ...getAuthHeader(),
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      callback(response.data.data_url);
    } catch (err) {
      alert('Error uploading image');
    }
  };

  // Logo Upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/upload-logo`, formData, {
        ...getAuthHeader(),
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSiteSettings({ ...siteSettings, logo_url: response.data.logo_url });
      alert('Logo uploaded successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error uploading logo';
      alert(errorMsg);
    } finally {
      setLogoUploading(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🖖</div>
            <h1 className="text-2xl font-bold text-white">Admin Login</h1>
            <p className="text-gray-400 mt-2">Nimoy-Knight Foundation CMS</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-[#141416] border border-gray-800 rounded-2xl p-8">
            {authError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6 text-red-500 text-sm text-center">
                {authError}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500"
                required
              />
            </div>
            
            <button
              type="submit"
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-colors"
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      {/* Header */}
      <header className="bg-[#141416] border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-2xl">🖖</span>
            <div>
              <h1 className="text-white font-semibold">Admin CMS</h1>
              <p className="text-gray-500 text-sm">Nimoy-Knight Foundation</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              View Site
            </button>
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1c] border border-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: 'honorees', label: 'Honorees', icon: Users },
            { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
            { id: 'initiatives', label: 'Initiatives', icon: Rocket },
            { id: 'partners', label: 'Partners', icon: Handshake },
            { id: 'news', label: 'News', icon: Newspaper },
            { id: 'homepage', label: 'Homepage', icon: Home },
            { id: 'special-events', label: 'Special Events', icon: Calendar },
            { id: 'signups', label: 'Signups', icon: Mail },
            { id: 'about', label: 'About Page', icon: FileText },
            { id: 'nominations', label: 'Nominations', icon: Users },
            { id: 'settings', label: 'Site Settings', icon: Settings },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-black'
                  : 'bg-[#141416] text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Honorees Tab */}
        {activeTab === 'honorees' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Manage Honorees ({honorees.length})</h2>
              <button
                onClick={() => setNewHonoree({ name: '', last_name: '', title: '', organization: '', image: '', quote: '', is_active: true, featured: false, social_facebook: '', social_linkedin: '', social_instagram: '', social_twitter: '', social_bluesky: '' })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Honoree
              </button>
            </div>

            {/* New Honoree Form */}
            {newHonoree && (
              <HonoreeForm
                honoree={newHonoree}
                onChange={setNewHonoree}
                onSave={() => saveHonoree(newHonoree, true)}
                onCancel={() => setNewHonoree(null)}
                onImageUpload={handleImageUpload}
                isNew
              />
            )}

            {/* Honorees List */}
            <div className="space-y-4">
              {honorees.map(honoree => (
                <div key={honoree.id} className="bg-[#141416] border border-gray-800 rounded-xl p-4">
                  {editingHonoree?.id === honoree.id ? (
                    <HonoreeForm
                      honoree={editingHonoree}
                      onChange={setEditingHonoree}
                      onSave={() => saveHonoree(editingHonoree)}
                      onCancel={() => setEditingHonoree(null)}
                      onImageUpload={handleImageUpload}
                    />
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                        {honoree.image && (
                          <img src={honoree.image} alt={honoree.name} className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold">{honoree.name}</h3>
                          {!honoree.is_active && (
                            <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">Hidden</span>
                          )}
                          {honoree.featured && (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded">⭐ Featured</span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm">{honoree.title}</p>
                        <p className="text-amber-500/70 text-sm">{honoree.organization}</p>
                        {honoree.quote && (
                          <p className="text-gray-400 text-sm mt-2 line-clamp-2">"{honoree.quote}"</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingHonoree(honoree)}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteHonoree(honoree.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Manage Testimonials ({testimonials.length})</h2>
              <button
                onClick={() => setNewTestimonial({ name: '', title: '', organization: '', image: '', quote: '', is_featured: false, is_active: true })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Testimonial
              </button>
            </div>

            {/* New Testimonial Form */}
            {newTestimonial && (
              <TestimonialForm
                testimonial={newTestimonial}
                onChange={setNewTestimonial}
                onSave={() => saveTestimonial(newTestimonial, true)}
                onCancel={() => setNewTestimonial(null)}
                onImageUpload={handleImageUpload}
                isNew
              />
            )}

            {/* Testimonials List */}
            <div className="space-y-4">
              {testimonials.map(testimonial => (
                <div key={testimonial.id} className="bg-[#141416] border border-gray-800 rounded-xl p-4">
                  {editingTestimonial?.id === testimonial.id ? (
                    <TestimonialForm
                      testimonial={editingTestimonial}
                      onChange={setEditingTestimonial}
                      onSave={() => saveTestimonial(editingTestimonial)}
                      onCancel={() => setEditingTestimonial(null)}
                      onImageUpload={handleImageUpload}
                    />
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-800 flex-shrink-0">
                        {testimonial.image && (
                          <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold">{testimonial.name}</h3>
                          {testimonial.is_featured && (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded">Featured</span>
                          )}
                          {!testimonial.is_active && (
                            <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">Hidden</span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm">{testimonial.title}</p>
                        <p className="text-amber-500/70 text-sm">{testimonial.organization}</p>
                        <p className="text-gray-400 text-sm mt-2 line-clamp-2">"{testimonial.quote}"</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingTestimonial(testimonial)}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteTestimonial(testimonial.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {testimonials.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No testimonials yet. Add your first testimonial above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Initiatives Tab */}
        {activeTab === 'initiatives' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Manage Initiatives ({initiatives.length})</h2>
              <button
                onClick={() => setNewInitiative({ title: '', subtitle: '', image: '', person_quote: '', person_name: '', foundation_quote: '', description: '', link_url: '', link_text: '', is_active: true, display_order: initiatives.length })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Initiative
              </button>
            </div>

            {/* New Initiative Form */}
            {newInitiative && (
              <InitiativeForm
                initiative={newInitiative}
                onChange={setNewInitiative}
                onSave={() => saveInitiative(newInitiative, true)}
                onCancel={() => setNewInitiative(null)}
                onImageUpload={handleImageUpload}
                isNew={true}
              />
            )}

            {/* Initiatives List */}
            <div className="space-y-4">
              {initiatives.map((initiative) => (
                <div key={initiative.id} className="bg-[#141416] border border-gray-800 rounded-xl p-4">
                  {editingInitiative?.id === initiative.id ? (
                    <InitiativeForm
                      initiative={editingInitiative}
                      onChange={setEditingInitiative}
                      onSave={() => saveInitiative(editingInitiative)}
                      onCancel={() => setEditingInitiative(null)}
                      onImageUpload={handleImageUpload}
                      isNew={false}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {initiative.image ? (
                          <img src={initiative.image} alt={initiative.title} className="w-20 h-20 object-cover rounded-lg" />
                        ) : (
                          <div className="w-20 h-20 bg-gray-800 rounded-lg flex items-center justify-center">
                            <Rocket className="w-8 h-8 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-white font-medium">{initiative.title}</h3>
                          {initiative.subtitle && (
                            <p className="text-amber-500 text-sm">{initiative.subtitle}</p>
                          )}
                          {initiative.person_name && (
                            <p className="text-gray-500 text-sm">Featuring: {initiative.person_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded ${initiative.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                          {initiative.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => setEditingInitiative({...initiative})}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteInitiative(initiative.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {initiatives.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No initiatives yet. Add your first initiative above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Manage Partners ({partners.length})</h2>
              <button
                onClick={() => setNewPartner({ name: '', logo: '', image: '', website: '', description: '', is_founding: true, is_active: true, display_order: partners.length })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Partner
              </button>
            </div>

            {/* New Partner Form */}
            {newPartner && (
              <PartnerForm
                partner={newPartner}
                onChange={setNewPartner}
                onSave={() => savePartner(newPartner, true)}
                onCancel={() => setNewPartner(null)}
                onImageUpload={handleImageUpload}
                isNew={true}
              />
            )}

            {/* Partners List */}
            <div className="space-y-4">
              {partners.map((partner) => (
                <div key={partner.id} className="bg-[#141416] border border-gray-800 rounded-xl p-4">
                  {editingPartner?.id === partner.id ? (
                    <PartnerForm
                      partner={editingPartner}
                      onChange={setEditingPartner}
                      onSave={() => savePartner(editingPartner)}
                      onCancel={() => setEditingPartner(null)}
                      onImageUpload={handleImageUpload}
                      isNew={false}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {partner.image ? (
                          <img src={partner.image} alt={partner.name} className="w-16 h-16 object-cover rounded-lg" />
                        ) : partner.logo ? (
                          <img src={partner.logo} alt={partner.name} className="w-16 h-12 object-contain" />
                        ) : (
                          <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center">
                            <Handshake className="w-6 h-6 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-white font-medium flex items-center gap-2">
                            {partner.name}
                            {partner.is_founding && (
                              <span className="px-2 py-0.5 bg-amber-500/10 text-amber-500 text-xs rounded-full">
                                Founding
                              </span>
                            )}
                          </h3>
                          <p className="text-gray-500 text-sm">{partner.website}</p>
                          <p className="text-gray-400 text-sm line-clamp-1">{partner.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded ${partner.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                          {partner.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => setEditingPartner({...partner})}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deletePartner(partner.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {partners.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No partners yet. Add your first partner above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Manage News Articles ({newsArticles.length})</h2>
              <button
                onClick={() => setNewNews({ 
                  headline: '', 
                  subheadline: '', 
                  source: '', 
                  author: '', 
                  pub_date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), 
                  location: '', 
                  summary: '', 
                  body_content: '', 
                  image_url: '', 
                  external_link: '', 
                  is_featured: false, 
                  is_active: true, 
                  display_order: 0 
                })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add News Article
              </button>
            </div>

            {/* New News Form */}
            {newNews && (
              <div className="bg-[#141416] border border-amber-500/50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">New News Article</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Headline *</label>
                    <input
                      type="text"
                      value={newNews.headline}
                      onChange={(e) => setNewNews({...newNews, headline: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Main headline of the article"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Subheadline</label>
                    <input
                      type="text"
                      value={newNews.subheadline}
                      onChange={(e) => setNewNews({...newNews, subheadline: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Optional subheadline or tagline"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Source/Publication</label>
                    <input
                      type="text"
                      value={newNews.source}
                      onChange={(e) => setNewNews({...newNews, source: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="e.g., Long Island Press"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Author</label>
                    <input
                      type="text"
                      value={newNews.author}
                      onChange={(e) => setNewNews({...newNews, author: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Author name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Publication Date</label>
                    <input
                      type="text"
                      value={newNews.pub_date}
                      onChange={(e) => setNewNews({...newNews, pub_date: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="e.g., January 29, 2026"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Location</label>
                    <input
                      type="text"
                      value={newNews.location}
                      onChange={(e) => setNewNews({...newNews, location: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="e.g., Los Angeles, CA"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Summary (Lead Paragraph)</label>
                    <textarea
                      value={newNews.summary}
                      onChange={(e) => setNewNews({...newNews, summary: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-24"
                      placeholder="Brief summary that appears in article previews"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Full Content</label>
                    <textarea
                      value={newNews.body_content}
                      onChange={(e) => setNewNews({...newNews, body_content: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-48"
                      placeholder="Full article content. Use double line breaks for paragraphs."
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Image</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newNews.image_url}
                        onChange={(e) => setNewNews({...newNews, image_url: e.target.value})}
                        className="flex-1 px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        placeholder="Image URL or upload"
                      />
                      <label className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors">
                        <Upload className="w-4 h-4" />
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, (url) => setNewNews({...newNews, image_url: url}))}
                        />
                      </label>
                    </div>
                    {newNews.image_url && (
                      <img src={newNews.image_url} alt="Preview" className="mt-2 h-20 object-cover rounded-lg" />
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">External Link</label>
                    <input
                      type="text"
                      value={newNews.external_link}
                      onChange={(e) => setNewNews({...newNews, external_link: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Link to original article (if applicable)"
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newNews.is_featured}
                        onChange={(e) => setNewNews({...newNews, is_featured: e.target.checked})}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className="text-gray-400">Featured Article</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newNews.is_active}
                        onChange={(e) => setNewNews({...newNews, is_active: e.target.checked})}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className="text-gray-400">Active</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Display Order</label>
                    <input
                      type="number"
                      value={newNews.display_order}
                      onChange={(e) => setNewNews({...newNews, display_order: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => saveNews(newNews, true)}
                    disabled={!newNews.headline}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    Save Article
                  </button>
                  <button
                    onClick={() => setNewNews(null)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* News Articles List */}
            <div className="space-y-4">
              {newsArticles.map((article) => (
                <div key={article.id} className="bg-[#141416] border border-gray-800 rounded-xl overflow-hidden">
                  {editingNews?.id === article.id ? (
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-gray-400 text-sm mb-1">Headline *</label>
                          <input
                            type="text"
                            value={editingNews.headline}
                            onChange={(e) => setEditingNews({...editingNews, headline: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-gray-400 text-sm mb-1">Subheadline</label>
                          <input
                            type="text"
                            value={editingNews.subheadline || ''}
                            onChange={(e) => setEditingNews({...editingNews, subheadline: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Source/Publication</label>
                          <input
                            type="text"
                            value={editingNews.source || ''}
                            onChange={(e) => setEditingNews({...editingNews, source: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Author</label>
                          <input
                            type="text"
                            value={editingNews.author || ''}
                            onChange={(e) => setEditingNews({...editingNews, author: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Publication Date</label>
                          <input
                            type="text"
                            value={editingNews.pub_date || ''}
                            onChange={(e) => setEditingNews({...editingNews, pub_date: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Location</label>
                          <input
                            type="text"
                            value={editingNews.location || ''}
                            onChange={(e) => setEditingNews({...editingNews, location: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-gray-400 text-sm mb-1">Summary</label>
                          <textarea
                            value={editingNews.summary || ''}
                            onChange={(e) => setEditingNews({...editingNews, summary: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-24"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-gray-400 text-sm mb-1">Full Content</label>
                          <textarea
                            value={editingNews.body_content || ''}
                            onChange={(e) => setEditingNews({...editingNews, body_content: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-48"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Image</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editingNews.image_url || ''}
                              onChange={(e) => setEditingNews({...editingNews, image_url: e.target.value})}
                              className="flex-1 px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                              placeholder="Image URL or upload"
                            />
                            <label className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors">
                              <Upload className="w-4 h-4" />
                              Upload
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (url) => setEditingNews({...editingNews, image_url: url}))}
                              />
                            </label>
                          </div>
                          {editingNews.image_url && (
                            <img src={editingNews.image_url} alt="Preview" className="mt-2 h-20 object-cover rounded-lg" />
                          )}
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">External Link</label>
                          <input
                            type="text"
                            value={editingNews.external_link || ''}
                            onChange={(e) => setEditingNews({...editingNews, external_link: e.target.value})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingNews.is_featured}
                              onChange={(e) => setEditingNews({...editingNews, is_featured: e.target.checked})}
                              className="w-4 h-4 accent-amber-500"
                            />
                            <span className="text-gray-400">Featured</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingNews.is_active}
                              onChange={(e) => setEditingNews({...editingNews, is_active: e.target.checked})}
                              className="w-4 h-4 accent-amber-500"
                            />
                            <span className="text-gray-400">Active</span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Display Order</label>
                          <input
                            type="number"
                            value={editingNews.display_order || 0}
                            onChange={(e) => setEditingNews({...editingNews, display_order: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => saveNews(editingNews)}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600"
                        >
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={() => setEditingNews(null)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        {article.image_url && (
                          <img
                            src={article.image_url}
                            alt={article.headline}
                            className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {article.is_featured && (
                              <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-500 rounded">Featured</span>
                            )}
                            {article.source && (
                              <span className="text-amber-500 text-sm">{article.source}</span>
                            )}
                            {article.pub_date && (
                              <span className="text-gray-500 text-sm">• {article.pub_date}</span>
                            )}
                          </div>
                          <h3 className="text-white font-semibold text-lg mb-1 truncate">{article.headline}</h3>
                          {article.subheadline && (
                            <p className="text-gray-400 text-sm mb-2">{article.subheadline}</p>
                          )}
                          {article.summary && (
                            <p className="text-gray-500 text-sm line-clamp-2">{article.summary}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${article.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                            {article.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => setEditingNews({...article})}
                            className="p-2 text-gray-400 hover:text-white transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteNews(article.id)}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {newsArticles.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No news articles yet. Add your first article above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Homepage Tab */}
        {activeTab === 'homepage' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Edit Homepage Content</h2>
              <button
                onClick={saveHomepageContent}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Save className="w-4 h-4" />
                Save All Changes
              </button>
            </div>

            <div className="space-y-8">
              {/* Hero Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Hero Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Title (Line 1)</label>
                    <input
                      type="text"
                      value={homepageContent.hero_title || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Nimoy-Knight"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Title (Line 2)</label>
                    <input
                      type="text"
                      value={homepageContent.hero_subtitle || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_subtitle: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Foundation"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Tagline</label>
                    <input
                      type="text"
                      value={homepageContent.hero_tagline || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_tagline: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Celebrating Leonard Nimoy's legacy..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Hero Quote</label>
                    <textarea
                      value={homepageContent.hero_quote || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_quote: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                      placeholder="A life is like a garden..."
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button 1 Text</label>
                    <input
                      type="text"
                      value={homepageContent.hero_button1_text || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_button1_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Discover His Legacy"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button 1 Link</label>
                    <input
                      type="text"
                      value={homepageContent.hero_button1_link || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_button1_link: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="/remembering-leonard"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button 2 Text</label>
                    <input
                      type="text"
                      value={homepageContent.hero_button2_text || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_button2_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Support the Foundation"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button 2 Link</label>
                    <input
                      type="text"
                      value={homepageContent.hero_button2_link || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, hero_button2_link: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="/donate"
                    />
                  </div>
                </div>
              </div>

              {/* Mission Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Mission Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Label</label>
                    <input
                      type="text"
                      value={homepageContent.mission_label || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, mission_label: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Our Mission"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Title</label>
                    <input
                      type="text"
                      value={homepageContent.mission_title || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, mission_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Live Long and Prosper"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Description</label>
                    <textarea
                      value={homepageContent.mission_description || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, mission_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-24"
                      placeholder="The Nimoy-Knight Foundation was established..."
                    />
                  </div>
                </div>
              </div>

              {/* Quote Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Quote Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Quote Text</label>
                    <textarea
                      value={homepageContent.quote_text || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, quote_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                      placeholder="The miracle is this: the more we share..."
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Quote Author</label>
                    <input
                      type="text"
                      value={homepageContent.quote_author || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, quote_author: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Leonard Nimoy"
                    />
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Call-to-Action Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">CTA Title</label>
                    <input
                      type="text"
                      value={homepageContent.cta_title || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, cta_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Join Us in Honoring Leonard's Memory"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">CTA Description</label>
                    <textarea
                      value={homepageContent.cta_description || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, cta_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                      placeholder="Your support helps us celebrate..."
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button Text</label>
                    <input
                      type="text"
                      value={homepageContent.cta_button_text || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, cta_button_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="Make a Donation"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button Link</label>
                    <input
                      type="text"
                      value={homepageContent.cta_button_link || ''}
                      onChange={(e) => setHomepageContent({...homepageContent, cta_button_link: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="/donate"
                    />
                  </div>
                </div>
              </div>

              {/* What We Do Cards */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  "What We Do" Cards
                </h3>
                <div className="space-y-6">
                  {/* Card 1 */}
                  <div className="border-b border-gray-800 pb-4">
                    <h4 className="text-white font-medium mb-3">Card 1: Honoring Leonard</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Title</label>
                        <input
                          type="text"
                          value={homepageContent.card1_title || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card1_title: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Link</label>
                        <input
                          type="text"
                          value={homepageContent.card1_link || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card1_link: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-gray-400 text-sm mb-1">Description</label>
                        <textarea
                          value={homepageContent.card1_description || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card1_description: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-16"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="border-b border-gray-800 pb-4">
                    <h4 className="text-white font-medium mb-3">Card 2: The Documentary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Title</label>
                        <input
                          type="text"
                          value={homepageContent.card2_title || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card2_title: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Link</label>
                        <input
                          type="text"
                          value={homepageContent.card2_link || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card2_link: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-gray-400 text-sm mb-1">Description</label>
                        <textarea
                          value={homepageContent.card2_description || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card2_description: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-16"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 3 */}
                  <div className="border-b border-gray-800 pb-4">
                    <h4 className="text-white font-medium mb-3">Card 3: LLAP Tribute Award</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Title</label>
                        <input
                          type="text"
                          value={homepageContent.card3_title || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card3_title: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Link</label>
                        <input
                          type="text"
                          value={homepageContent.card3_link || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card3_link: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-gray-400 text-sm mb-1">Description</label>
                        <textarea
                          value={homepageContent.card3_description || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card3_description: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-16"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 4 */}
                  <div>
                    <h4 className="text-white font-medium mb-3">Card 4: The Anniversary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Title</label>
                        <input
                          type="text"
                          value={homepageContent.card4_title || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card4_title: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Link</label>
                        <input
                          type="text"
                          value={homepageContent.card4_link || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card4_link: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-gray-400 text-sm mb-1">Description</label>
                        <textarea
                          value={homepageContent.card4_description || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, card4_description: e.target.value})}
                          className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-16"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gallery Images */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Gallery Images
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((num) => (
                    <div key={num}>
                      <label className="block text-gray-400 text-sm mb-1">Image {num}</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={homepageContent[`gallery_image${num}`] || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, [`gallery_image${num}`]: e.target.value})}
                          className="flex-1 px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                          placeholder="Image URL"
                        />
                        <label className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors">
                          <Upload className="w-4 h-4" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(e, (url) => setHomepageContent({...homepageContent, [`gallery_image${num}`]: url}))}
                          />
                        </label>
                      </div>
                      {homepageContent[`gallery_image${num}`] && (
                        <img src={homepageContent[`gallery_image${num}`]} alt={`Gallery ${num}`} className="mt-2 h-20 object-cover rounded-lg" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveHomepageContent}
                  className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
                >
                  <Save className="w-5 h-5" />
                  Save All Homepage Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Special Events Tab */}
        {activeTab === 'special-events' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Edit Special Events Page</h2>
              <button
                onClick={saveSpecialEventsContent}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Save className="w-4 h-4" />
                Save All Changes
              </button>
            </div>

            <div className="space-y-8">
              {/* Hero Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Hero Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Label (e.g., "February 27th • 10th Anniversary")</label>
                    <input
                      type="text"
                      value={specialEventsContent.hero_label || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, hero_label: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Page Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.hero_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, hero_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Description</label>
                    <textarea
                      value={specialEventsContent.hero_description || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, hero_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-24"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Hashtag Text</label>
                    <input
                      type="text"
                      value={specialEventsContent.hero_hashtag_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, hero_hashtag_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Tagline</label>
                    <input
                      type="text"
                      value={specialEventsContent.hero_tagline || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, hero_tagline: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Hero Image</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={specialEventsContent.hero_image || ''}
                        onChange={(e) => setSpecialEventsContent({...specialEventsContent, hero_image: e.target.value})}
                        className="flex-1 px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        placeholder="Image URL"
                      />
                      <label className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors">
                        <Upload className="w-4 h-4" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, (url) => setSpecialEventsContent({...specialEventsContent, hero_image: url}))}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gift Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Gift/Donation Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Section Label</label>
                    <input
                      type="text"
                      value={specialEventsContent.gift_label || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_label: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.gift_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Description</label>
                    <textarea
                      value={specialEventsContent.gift_description || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button Text</label>
                    <input
                      type="text"
                      value={specialEventsContent.gift_button_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_button_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button Link</label>
                    <input
                      type="text"
                      value={specialEventsContent.gift_button_link || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_button_link: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Typeform ID</label>
                    <input
                      type="text"
                      value={specialEventsContent.gift_typeform_id || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_typeform_id: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                      placeholder="e.g., 01KETY7YEZQAK5HEY3QMQR31CD"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Gift Image</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={specialEventsContent.gift_image || ''}
                        onChange={(e) => setSpecialEventsContent({...specialEventsContent, gift_image: e.target.value})}
                        className="flex-1 px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                        placeholder="Image URL"
                      />
                      <label className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors">
                        <Upload className="w-4 h-4" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, (url) => setSpecialEventsContent({...specialEventsContent, gift_image: url}))}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remembering Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Remembering Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Section Label</label>
                    <input
                      type="text"
                      value={specialEventsContent.remember_label || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_label: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.remember_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Description</label>
                    <textarea
                      value={specialEventsContent.remember_description || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Card 1 Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.remember_card1_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_card1_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Card 2 Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.remember_card2_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_card2_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Card 1 Text</label>
                    <textarea
                      value={specialEventsContent.remember_card1_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_card1_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Card 2 Text</label>
                    <textarea
                      value={specialEventsContent.remember_card2_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, remember_card2_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                    />
                  </div>
                </div>
              </div>

              {/* Share Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Share Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Section Label</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_label || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_label: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Description</label>
                    <textarea
                      value={specialEventsContent.share_description || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-16"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Step 1 Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_step1_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_step1_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Step 1 Text</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_step1_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_step1_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Step 2 Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_step2_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_step2_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Step 2 Text</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_step2_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_step2_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Step 3 Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_step3_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_step3_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Step 3 Text</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_step3_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_step3_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Hashtag 1</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_hashtag1 || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_hashtag1: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Hashtag 2</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_hashtag2 || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_hashtag2: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Hashtag 3</label>
                    <input
                      type="text"
                      value={specialEventsContent.share_hashtag3 || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, share_hashtag3: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Quote Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Quote Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Quote Text</label>
                    <textarea
                      value={specialEventsContent.quote_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, quote_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-20"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Quote Attribution</label>
                    <input
                      type="text"
                      value={specialEventsContent.quote_attribution || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, quote_attribution: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-[#141416] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Call-to-Action Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">CTA Title</label>
                    <input
                      type="text"
                      value={specialEventsContent.cta_title || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, cta_title: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">CTA Description</label>
                    <textarea
                      value={specialEventsContent.cta_description || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, cta_description: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white h-16"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button Text</label>
                    <input
                      type="text"
                      value={specialEventsContent.cta_button_text || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, cta_button_text: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Button Link</label>
                    <input
                      type="text"
                      value={specialEventsContent.cta_button_link || ''}
                      onChange={(e) => setSpecialEventsContent({...specialEventsContent, cta_button_link: e.target.value})}
                      className="w-full px-3 py-2 bg-[#0a0a0b] border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveSpecialEventsContent}
                  className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
                >
                  <Save className="w-5 h-5" />
                  Save All Special Events Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Signups Tab */}
        {activeTab === 'signups' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Anniversary Signups ({signups.length})</h2>
              <button
                onClick={() => {
                  const csvContent = "Name,Email,Signed Up\n" + signups.map(s => 
                    `"${s.name}","${s.email}","${s.created_at}"`
                  ).join("\n");
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `anniversary-signups-${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
                disabled={signups.length === 0}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            <div className="bg-[#141416] border border-gray-800 rounded-xl overflow-hidden">
              {signups.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No signups yet. People will appear here when they sign up on the homepage.
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-[#1a1a1c] border-b border-gray-800">
                    <tr>
                      <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Name</th>
                      <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Email</th>
                      <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Signed Up</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {signups.map((signup, index) => (
                      <tr key={signup.id || index} className="hover:bg-[#1a1a1c] transition-colors">
                        <td className="px-6 py-4 text-white">{signup.name}</td>
                        <td className="px-6 py-4">
                          <a href={`mailto:${signup.email}`} className="text-amber-500 hover:text-amber-400">
                            {signup.email}
                          </a>
                        </td>
                        <td className="px-6 py-4 text-gray-400">
                          {new Date(signup.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="text-gray-500 text-sm mt-4">
              These are people who signed up for the Feb. 27 Anniversary event on the homepage.
            </p>

            {/* Newsletter Signups Section */}
            <div className="mt-12 pt-8 border-t border-gray-800">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Newsletter Signups ({newsletterSignups.length})</h2>
                <button
                  onClick={() => {
                    const csvContent = "Name,Email,Signed Up\n" + newsletterSignups.map(s => 
                      `"${s.name}","${s.email}","${s.created_at}"`
                    ).join("\n");
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `newsletter-signups-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
                  disabled={newsletterSignups.length === 0}
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>

              <div className="bg-[#141416] border border-gray-800 rounded-xl overflow-hidden">
                {newsletterSignups.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No newsletter signups yet. People will appear here when they subscribe on the Initiatives page.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-[#1a1a1c] border-b border-gray-800">
                      <tr>
                        <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Name</th>
                        <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Email</th>
                        <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Subscribed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {newsletterSignups.map((signup, index) => (
                        <tr key={signup.id || index} className="hover:bg-[#1a1a1c] transition-colors">
                          <td className="px-6 py-4 text-white">{signup.name}</td>
                          <td className="px-6 py-4">
                            <a href={`mailto:${signup.email}`} className="text-amber-500 hover:text-amber-400">
                              {signup.email}
                            </a>
                          </td>
                          <td className="px-6 py-4 text-gray-400">
                            {new Date(signup.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <p className="text-gray-500 text-sm mt-4">
                These are people who subscribed to the newsletter on the Initiatives page.
              </p>
            </div>
          </div>
        )}

        {/* About Page Tab */}
        {activeTab === 'about' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Edit About Page</h2>
              <button
                onClick={saveAboutContent}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>

            <div className="bg-[#141416] border border-gray-800 rounded-xl p-6 space-y-6">
              {/* Founders Image */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Founders Image</label>
                <div className="flex items-center gap-4">
                  {aboutContent.founders_image && (
                    <img src={aboutContent.founders_image} alt="Founders" className="w-32 h-32 object-cover rounded-lg" />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1c] border border-gray-800 rounded-lg text-gray-400 hover:text-white cursor-pointer transition-colors">
                    <Upload className="w-4 h-4" />
                    Upload Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, (url) => setAboutContent({...aboutContent, founders_image: url}))}
                    />
                  </label>
                </div>
              </div>

              {/* Founders Names */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Founders Names</label>
                <input
                  type="text"
                  value={aboutContent.founders_names || ''}
                  onChange={(e) => setAboutContent({...aboutContent, founders_names: e.target.value})}
                  className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Page Content (supports line breaks)</label>
                <textarea
                  value={aboutContent.content || ''}
                  onChange={(e) => setAboutContent({...aboutContent, content: e.target.value})}
                  rows={15}
                  className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 resize-none"
                  placeholder="Enter the about page content. Use double line breaks for paragraphs."
                />
              </div>
            </div>
          </div>
        )}

        {/* Nominations Tab */}
        {activeTab === 'nominations' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6">Review Nominations ({nominations.length})</h2>
            
            <div className="space-y-4">
              {nominations.map(nomination => (
                <div key={nomination.id} className="bg-[#141416] border border-gray-800 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-white font-semibold">{nomination.nominee_name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          nomination.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          nomination.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {nomination.status}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm">Nominated by: {nomination.your_name} ({nomination.your_email})</p>
                      <p className="text-gray-500 text-sm">Nominee email: {nomination.nominee_email}</p>
                      {nomination.organization && (
                        <p className="text-amber-500/70 text-sm">Organization: {nomination.organization}</p>
                      )}
                      <p className="text-gray-400 text-sm mt-2">{nomination.reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => axios.put(`${API}/admin/nominations/${nomination.id}/status?status=approved`, {}, getAuthHeader()).then(fetchAllData)}
                        className="p-2 text-gray-400 hover:text-green-500 transition-colors"
                        title="Approve"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => axios.put(`${API}/admin/nominations/${nomination.id}/status?status=rejected`, {}, getAuthHeader()).then(fetchAllData)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Reject"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {nominations.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No nominations to review.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Site Settings</h2>
            </div>

            <div className="bg-[#141416] border border-gray-800 rounded-xl p-6 space-y-6">
              {/* Logo Upload */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-amber-500" />
                  Homepage Logo
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Upload a new logo to display on the homepage. Recommended size: 200x200 pixels. Max file size: 2MB.
                </p>
                
                <div className="flex flex-col md:flex-row items-start gap-6">
                  {/* Current Logo Preview */}
                  <div className="flex-shrink-0">
                    <p className="text-gray-500 text-xs mb-2">Current Logo:</p>
                    <div className="w-40 h-40 bg-[#1a1a1c] border border-gray-800 rounded-2xl overflow-hidden flex items-center justify-center">
                      {siteSettings.logo_url ? (
                        <img 
                          src={siteSettings.logo_url} 
                          alt="Current Logo" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-4xl">🖖</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Upload Area */}
                  <div className="flex-1">
                    <label className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${
                      logoUploading 
                        ? 'border-amber-500/50 bg-amber-500/5' 
                        : 'border-gray-700 hover:border-amber-500/50 hover:bg-[#1a1a1c]'
                    }`}>
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {logoUploading ? (
                          <>
                            <RefreshCw className="w-10 h-10 text-amber-500 mb-3 animate-spin" />
                            <p className="text-sm text-amber-500">Uploading...</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-10 h-10 text-gray-500 mb-3" />
                            <p className="mb-2 text-sm text-gray-400">
                              <span className="font-semibold text-amber-500">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 2MB</p>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={logoUploading}
                      />
                    </label>
                  </div>
                </div>

                {/* Manual URL Input */}
                <div className="mt-6">
                  <label className="block text-gray-400 text-sm mb-2">Or enter image URL directly:</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={siteSettings.logo_url || ''}
                      onChange={(e) => setSiteSettings({...siteSettings, logo_url: e.target.value})}
                      placeholder="https://example.com/logo.png"
                      className="flex-1 px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500"
                    />
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/admin/site-settings?logo_url=${encodeURIComponent(siteSettings.logo_url)}`, {}, getAuthHeader());
                          alert('Logo URL saved successfully!');
                        } catch (err) {
                          alert('Error saving logo URL');
                        }
                      }}
                      className="px-6 py-3 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors"
                    >
                      Save URL
                    </button>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-500 text-sm">
                  <strong>Note:</strong> After uploading a new logo, refresh the homepage to see the changes.
                  The logo appears in the hero section of the homepage.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Honoree Form Component
const HonoreeForm = ({ honoree, onChange, onSave, onCancel, onImageUpload, isNew }) => (
  <div className="bg-[#1a1a1c] rounded-lg p-4 mb-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-400 text-sm mb-1">Full Name *</label>
        <input
          type="text"
          value={honoree.name}
          onChange={(e) => onChange({...honoree, name: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Last Name (for sorting) *</label>
        <input
          type="text"
          value={honoree.last_name}
          onChange={(e) => onChange({...honoree, last_name: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Title *</label>
        <input
          type="text"
          value={honoree.title}
          onChange={(e) => onChange({...honoree, title: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Organization</label>
        <input
          type="text"
          value={honoree.organization}
          onChange={(e) => onChange({...honoree, organization: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>
    </div>
    
    <div>
      <label className="block text-gray-400 text-sm mb-1">Image</label>
      <div className="flex items-center gap-4">
        {honoree.image && (
          <img src={honoree.image} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
        )}
        <input
          type="text"
          value={honoree.image}
          onChange={(e) => onChange({...honoree, image: e.target.value})}
          placeholder="Image URL or upload"
          className="flex-1 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
        <label className="flex items-center gap-2 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-gray-400 hover:text-white cursor-pointer transition-colors text-sm">
          <Upload className="w-4 h-4" />
          Upload
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onImageUpload(e, (url) => onChange({...honoree, image: url}))}
          />
        </label>
      </div>
    </div>
    
    <div>
      <label className="block text-gray-400 text-sm mb-1">Quote/Testimonial</label>
      <textarea
        value={honoree.quote}
        onChange={(e) => onChange({...honoree, quote: e.target.value})}
        rows={3}
        className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 resize-none"
      />
    </div>

    {/* Social Media Links Section */}
    <div className="border-t border-gray-800 pt-4">
      <h4 className="text-amber-500 text-sm font-medium mb-3 flex items-center gap-2">
        📱 Social Media Links
        <span className="text-gray-500 font-normal">(for Featured Honoree)</span>
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Facebook URL</label>
          <input
            type="url"
            value={honoree.social_facebook || ''}
            onChange={(e) => onChange({...honoree, social_facebook: e.target.value})}
            placeholder="https://facebook.com/..."
            className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">LinkedIn URL</label>
          <input
            type="url"
            value={honoree.social_linkedin || ''}
            onChange={(e) => onChange({...honoree, social_linkedin: e.target.value})}
            placeholder="https://linkedin.com/in/..."
            className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Instagram URL</label>
          <input
            type="url"
            value={honoree.social_instagram || ''}
            onChange={(e) => onChange({...honoree, social_instagram: e.target.value})}
            placeholder="https://instagram.com/..."
            className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">X (Twitter) URL</label>
          <input
            type="url"
            value={honoree.social_twitter || ''}
            onChange={(e) => onChange({...honoree, social_twitter: e.target.value})}
            placeholder="https://x.com/..."
            className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-gray-400 text-xs mb-1">Bluesky URL</label>
          <input
            type="url"
            value={honoree.social_bluesky || ''}
            onChange={(e) => onChange({...honoree, social_bluesky: e.target.value})}
            placeholder="https://bsky.app/profile/..."
            className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
      </div>
    </div>
    
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={honoree.is_active}
          onChange={(e) => onChange({...honoree, is_active: e.target.checked})}
          className="rounded border-gray-600"
        />
        Active (visible on site)
      </label>
      <label className="flex items-center gap-2 text-amber-500 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={honoree.featured || false}
          onChange={(e) => onChange({...honoree, featured: e.target.checked})}
          className="rounded border-amber-500"
        />
        ⭐ Featured Honoree
      </label>
    </div>
    
    <div className="flex gap-2 pt-2">
      <button
        onClick={onSave}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors text-sm"
      >
        <Save className="w-4 h-4" />
        {isNew ? 'Add Honoree' : 'Save Changes'}
      </button>
      <button
        onClick={onCancel}
        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
      >
        Cancel
      </button>
    </div>
  </div>
);

// Testimonial Form Component
const TestimonialForm = ({ testimonial, onChange, onSave, onCancel, onImageUpload, isNew }) => (
  <div className="bg-[#1a1a1c] rounded-lg p-4 mb-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-400 text-sm mb-1">Name *</label>
        <input
          type="text"
          value={testimonial.name}
          onChange={(e) => onChange({...testimonial, name: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Title *</label>
        <input
          type="text"
          value={testimonial.title}
          onChange={(e) => onChange({...testimonial, title: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Organization</label>
        <input
          type="text"
          value={testimonial.organization}
          onChange={(e) => onChange({...testimonial, organization: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Image</label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={testimonial.image}
            onChange={(e) => onChange({...testimonial, image: e.target.value})}
            placeholder="Image URL"
            className="flex-1 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
          <label className="p-2 bg-[#141416] border border-gray-800 rounded-lg text-gray-400 hover:text-white cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onImageUpload(e, (url) => onChange({...testimonial, image: url}))}
            />
          </label>
        </div>
      </div>
    </div>
    
    <div>
      <label className="block text-gray-400 text-sm mb-1">Quote *</label>
      <textarea
        value={testimonial.quote}
        onChange={(e) => onChange({...testimonial, quote: e.target.value})}
        rows={3}
        className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 resize-none"
        required
      />
    </div>
    
    <div className="flex items-center gap-6">
      <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={testimonial.is_featured}
          onChange={(e) => onChange({...testimonial, is_featured: e.target.checked})}
          className="rounded border-gray-600"
        />
        Featured
      </label>
      <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={testimonial.is_active}
          onChange={(e) => onChange({...testimonial, is_active: e.target.checked})}
          className="rounded border-gray-600"
        />
        Active
      </label>
    </div>
    
    <div className="flex gap-2 pt-2">
      <button
        onClick={onSave}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors text-sm"
      >
        <Save className="w-4 h-4" />
        {isNew ? 'Add Testimonial' : 'Save Changes'}
      </button>
      <button
        onClick={onCancel}
        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
      >
        Cancel
      </button>
    </div>
  </div>
);

// Partner Form Component
const PartnerForm = ({ partner, onChange, onSave, onCancel, onImageUpload, isNew }) => (
  <div className="bg-[#1a1a1c] rounded-lg p-4 mb-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-400 text-sm mb-1">Partner Name *</label>
        <input
          type="text"
          value={partner.name}
          onChange={(e) => onChange({...partner, name: e.target.value})}
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Website URL</label>
        <input
          type="url"
          value={partner.website}
          onChange={(e) => onChange({...partner, website: e.target.value})}
          placeholder="https://..."
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-400 text-sm mb-1">Logo URL</label>
        <div className="flex items-center gap-2">
          {partner.logo && (
            <img src={partner.logo} alt="Logo preview" className="w-12 h-8 object-contain bg-white rounded" />
          )}
          <input
            type="text"
            value={partner.logo}
            onChange={(e) => onChange({...partner, logo: e.target.value})}
            placeholder="Logo image URL"
            className="flex-1 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
          <label className="p-2 bg-[#141416] border border-gray-800 rounded-lg text-gray-400 hover:text-white cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onImageUpload(e, (url) => onChange({...partner, logo: url}))}
            />
          </label>
        </div>
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Featured Image</label>
        <div className="flex items-center gap-2">
          {partner.image && (
            <img src={partner.image} alt="Image preview" className="w-12 h-12 object-cover rounded" />
          )}
          <input
            type="text"
            value={partner.image}
            onChange={(e) => onChange({...partner, image: e.target.value})}
            placeholder="Featured image URL"
            className="flex-1 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          />
          <label className="p-2 bg-[#141416] border border-gray-800 rounded-lg text-gray-400 hover:text-white cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onImageUpload(e, (url) => onChange({...partner, image: url}))}
            />
          </label>
        </div>
      </div>
    </div>

    <div>
      <label className="block text-gray-400 text-sm mb-1">Description *</label>
      <textarea
        value={partner.description}
        onChange={(e) => onChange({...partner, description: e.target.value})}
        rows={4}
        placeholder="Describe what this partner does..."
        className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 resize-none"
        required
      />
    </div>

    <div className="flex items-center gap-6">
      <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={partner.is_active}
          onChange={(e) => onChange({...partner, is_active: e.target.checked})}
          className="rounded border-gray-600"
        />
        Active (visible on site)
      </label>
      <label className="flex items-center gap-2 text-amber-500 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={partner.is_founding}
          onChange={(e) => onChange({...partner, is_founding: e.target.checked})}
          className="rounded border-amber-500"
        />
        ⭐ Founding Partner
      </label>
    </div>
    
    <div className="flex gap-2 pt-2">
      <button
        onClick={onSave}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors text-sm"
      >
        <Save className="w-4 h-4" />
        {isNew ? 'Add Partner' : 'Save Changes'}
      </button>
      <button
        onClick={onCancel}
        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
      >
        Cancel
      </button>
    </div>
  </div>
);

// Initiative Form Component
const InitiativeForm = ({ initiative, onChange, onSave, onCancel, onImageUpload, isNew }) => (
  <div className="bg-[#1a1a1c] rounded-lg p-4 mb-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-400 text-sm mb-1">Title *</label>
        <input
          type="text"
          value={initiative.title}
          onChange={(e) => onChange({...initiative, title: e.target.value})}
          placeholder="e.g., Send Jessica To Space"
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          required
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Subtitle</label>
        <input
          type="text"
          value={initiative.subtitle}
          onChange={(e) => onChange({...initiative, subtitle: e.target.value})}
          placeholder="e.g., The First Openly Autistic Woman In Space"
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>
    </div>

    <div>
      <label className="block text-gray-400 text-sm mb-1">Image</label>
      <div className="flex items-center gap-4">
        {initiative.image && (
          <img src={initiative.image} alt="Preview" className="w-20 h-20 object-cover rounded-lg" />
        )}
        <input
          type="text"
          value={initiative.image}
          onChange={(e) => onChange({...initiative, image: e.target.value})}
          placeholder="Image URL"
          className="flex-1 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
        <label className="flex items-center gap-2 px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-gray-400 hover:text-white cursor-pointer transition-colors text-sm">
          <Upload className="w-4 h-4" />
          Upload
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onImageUpload(e, (url) => onChange({...initiative, image: url}))}
          />
        </label>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-400 text-sm mb-1">Person Name</label>
        <input
          type="text"
          value={initiative.person_name}
          onChange={(e) => onChange({...initiative, person_name: e.target.value})}
          placeholder="e.g., Jessica Schonhut-Stasik"
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>
      <div>
        <label className="block text-gray-400 text-sm mb-1">Link URL</label>
        <input
          type="url"
          value={initiative.link_url}
          onChange={(e) => onChange({...initiative, link_url: e.target.value})}
          placeholder="https://..."
          className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>
    </div>

    <div>
      <label className="block text-gray-400 text-sm mb-1">Person Quote</label>
      <textarea
        value={initiative.person_quote}
        onChange={(e) => onChange({...initiative, person_quote: e.target.value})}
        rows={3}
        placeholder="Quote from the person featured in this initiative..."
        className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 resize-none"
      />
    </div>

    <div>
      <label className="block text-gray-400 text-sm mb-1">Foundation Quote</label>
      <textarea
        value={initiative.foundation_quote}
        onChange={(e) => onChange({...initiative, foundation_quote: e.target.value})}
        rows={2}
        placeholder="Quote from the Nimoy Knight Foundation..."
        className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 resize-none"
      />
    </div>

    <div>
      <label className="block text-gray-400 text-sm mb-1">Description (optional)</label>
      <textarea
        value={initiative.description}
        onChange={(e) => onChange({...initiative, description: e.target.value})}
        rows={2}
        placeholder="Additional details about the initiative..."
        className="w-full px-3 py-2 bg-[#141416] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 resize-none"
      />
    </div>

    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={initiative.is_active}
          onChange={(e) => onChange({...initiative, is_active: e.target.checked})}
          className="rounded border-gray-600"
        />
        Active (visible on site)
      </label>
    </div>
    
    <div className="flex gap-2 pt-2">
      <button
        onClick={onSave}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-600 transition-colors text-sm"
      >
        <Save className="w-4 h-4" />
        {isNew ? 'Add Initiative' : 'Save Changes'}
      </button>
      <button
        onClick={onCancel}
        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
      >
        Cancel
      </button>
    </div>
  </div>
);

export default AdminPage;
